import { useCallback, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import {
  IncidentDocument,
  useCreateIncidentActionMutation,
} from '@/graphql/generated/hooks'
import {
  IncidentActionType,
  IncidentPriority,
} from '@/graphql/generated/schemas'

interface UseUpdateIncidentPriorityIProps {
  priority?: IncidentPriority
}
export const useUpdateIncidentPriority = ({
  priority,
}: UseUpdateIncidentPriorityIProps) => {
  const [createIncidentAction, { loading: isUpdating }] =
    useCreateIncidentActionMutation()
  const [currentPriority, setCurrentPriority] =
    useState<IncidentPriority>(priority)
  const { showError } = useToasts({
    preventDuplicate: true,
    defaultErrorMessage: 'Failed to update priority.',
  })

  const updatePriority = useCallback(
    async (incidentId: string, newPriority: IncidentPriority) => {
      try {
        await createIncidentAction({
          variables: {
            input: {
              incidentId,
              type: IncidentActionType.PriorityChange,
              priority: newPriority,
            },
          },
          refetchQueries: [
            { query: IncidentDocument, variables: { id: incidentId } },
          ],
          awaitRefetchQueries: true,
        })
        setCurrentPriority(newPriority)
      } catch (error) {
        Sentry.captureException(error)
        showError(error?.message)
      }
    },
    [createIncidentAction, showError]
  )

  return { updatePriority, currentPriority, isUpdating }
}
